import type React from 'react';
import { useRef } from 'react';
import { useIntl } from 'react-intl';
import { Link } from '@m/alchemy-ui/Link';
import { Popper } from '@m/alchemy-ui/Popper';
import { type AuthorListPopupProps } from '../../features';
import { useHoverTrigger } from '../../hooks/useHoverTrigger';
import { usePopperFocusTrap } from '../../hooks/usePopperFocusTrap';
import { AuthorAvatar, AuthorName, AvatarImage, Popup, Title } from './AuthorListPopup.styled';

declare function popUp(url: string, options: unknown): void;

export const AuthorListPopup = ({ title, data, arrowPlacement = 'top', triggerId }: AuthorListPopupProps) => {
  const intl = useIntl();
  const { triggerRef, isOpen, open, close } = useHoverTrigger(triggerId);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const { handlePopupKeyDown } = usePopperFocusTrap(isOpen, triggerRef, popupRef, close, triggerId);

  const openAuthorProfilePopup = (author: AuthorListPopupProps['data'][number]) => {
    return (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      popUp(`/user/view/${author.id}`, {
        popUpTitle: intl.formatMessage({ id: 'profile', defaultMessage: 'Profile' }),
        height: 390,
        focusOnClose: event.currentTarget
      });
    };
  };

  const placement = arrowPlacement === 'top' ? 'bottom' : 'top';

  return (
    <Popper
      placement={placement}
      isOpen={isOpen}
      popoverOptions={{ mode: 'manual' }}
      triggerRef={triggerRef}
      overlay={
        <Popup
          ref={popupRef}
          hasPadding={false}
          hasArrow
          data-arrow={arrowPlacement}
          onMouseEnter={open}
          onMouseLeave={close}
          onFocusCapture={open}
          onBlurCapture={close}
          onKeyDownCapture={handlePopupKeyDown}
        >
          <Title>{title}</Title>
          <ul>
            {data.map((author) => (
              <li key={author.id}>
                <AuthorAvatar>
                  <AvatarImage src={author.avatarImageUrl} alt="" aria-hidden="true" />
                </AuthorAvatar>
                <AuthorName>
                  <Link href="#" onClick={openAuthorProfilePopup(author)}>
                    {author.name}
                  </Link>
                </AuthorName>
              </li>
            ))}
          </ul>
        </Popup>
      }
    />
  );
};
